/**
 * Onboarding hint — a non-secret localStorage flag that mirrors "is the
 * signed-in user's profile incomplete?" for the lifetime of one account.
 *
 * Companion to session-hint.ts. The session hint answers "am I signed
 * in?"; this hint answers "am I signed in but still need to fill in my
 * name?". Same constraints, same rationale: the SPA cannot call
 * /auth/get-session to ask, because that endpoint returns the session
 * token in its response body and we don't want to make session-token
 * exfiltration reachable from JavaScript. See
 * apps/client/docs/overview.md §"Auth surface" for the broader pattern.
 *
 * Lifecycle:
 *   - SET   by the OTP verify success handler when the response's
 *           `user.name` is null or empty (the Better Auth signal that
 *           the user row was just provisioned). Triggers a /onboarding
 *           redirect at the route level.
 *   - CLEAR by the onboarding form on successful updateName, and as
 *           belt-and-suspenders on sign-out (a re-sign-in starts fresh).
 *
 * The flag is purely a UI cache. The server is still authoritative
 * about whether a user row has a name — if a future feature wants to
 * gate on it server-side, it should read the row, not the hint.
 *
 * Like the session hint, we live in localStorage (not sessionStorage)
 * so the flag survives a browser restart. Cross-tab consistency is
 * handled by dispatching a synthetic StorageEvent on the same tab as
 * well as relying on the browser's native cross-tab event.
 */

const HINT_KEY = 'str.needs_onboarding';

function isStorageAvailable(): boolean {
  // See session-hint.ts for the same rationale — storage can throw in
  // privacy-mode browsers or in non-browser test runtimes. Falling back
  // to "no hint" means the home route doesn't redirect to /onboarding,
  // which is the safer default (the user can navigate manually).
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch {
    return false;
  }
}

/** Is the onboarding hint currently set? Synchronous read of localStorage. */
export function hasOnboardingHint(): boolean {
  if (!isStorageAvailable()) return false;
  try {
    return window.localStorage.getItem(HINT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Record that the signed-in user has just been provisioned without a
 * name and should be routed through onboarding. Safe to call repeatedly.
 * Dispatches a synthetic 'storage' event so same-tab subscribers
 * re-render (the native event only fires in *other* tabs).
 */
export function setOnboardingHint(): void {
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.setItem(HINT_KEY, '1');
    dispatchLocalStorageEvent('1');
  } catch {
    // Quota exceeded / blocked — the UI falls back to "no onboarding
    // needed", which lets the user past the gate. Degraded but not
    // broken: they can edit their name through future profile UI.
  }
}

/** Record that onboarding has been completed (or was never needed). */
export function clearOnboardingHint(): void {
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.removeItem(HINT_KEY);
    dispatchLocalStorageEvent(null);
  } catch {
    // Same fallback — best-effort.
  }
}

function dispatchLocalStorageEvent(newValue: string | null): void {
  try {
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: HINT_KEY,
        newValue,
        storageArea: window.localStorage,
      }),
    );
  } catch {
    // StorageEvent constructor unavailable in very old runtimes; the
    // value is still written and cross-tab subscribers will pick it up
    // on the next native event.
  }
}

/**
 * The localStorage key, exported for tests that need to manipulate the
 * value directly or assert on it. Production code should call the
 * has/set/clear functions above.
 */
export const ONBOARDING_HINT_KEY = HINT_KEY;
